import React, { useEffect, useState } from 'react';
import { Button, Table, TableHead, TableRow, TableCell, TableBody, Dialog, DialogTitle, DialogContent, DialogActions, Select, MenuItem, FormControl, InputLabel, Stepper, Step, StepLabel, Box, Card, CardContent, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { ProcedureService } from '../../services/procedureService';
// import { ProcedureStatus } from '../../types/procedures';
import { getPatient } from '../../services/patientService';
import { ORDER_TYPE_STATUS_FLOW } from '../../types/statusFlows';

const procedureService = new ProcedureService();

// Styled Components - Purple/Blue Gradient Header
const HeaderCard = styled(Card)(({ theme }) => ({
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: 'white',
  marginBottom: theme.spacing(3),
  borderRadius: '16px',
  boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)',
}));

export default function OrderStatus() {
  const [orders, setOrders] = useState([]);
  const [patientMap, setPatientMap] = useState({});
  const [open, setOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [newStatus, setNewStatus] = useState('');

  useEffect(() => {
    async function fetchData() {
      const procedures = await procedureService.getPatientProcedures();
      setOrders(procedures);
      // Fetch patient names
      const uniquePatientIds = Array.from(new Set(procedures.map(p => p.patientId).filter(Boolean)));
      const patientEntries = await Promise.all(
        uniquePatientIds.map(async (id) => {
          const patient = await getPatient(id);
          return [id, patient ? patient.name : id];
        })
      );
      setPatientMap(Object.fromEntries(patientEntries));
    }
    fetchData();
  }, []);

  const handleOpen = (order) => {
    setSelectedOrder(order);
    setNewStatus(order.status);
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  const handleStatusChange = (e) => setNewStatus(e.target.value);

  const handleUpdateStatus = async () => {
    // You may want to implement an update method in procedureService
    // For now, just close the dialog and refresh
    setOrders(await procedureService.getPatientProcedures());
    handleClose();
  };

  return (
    <Box sx={{ p: 3 }}>
      <HeaderCard>
        <CardContent sx={{ py: 1.5, px: 3, '&:last-child': { pb: 1.5 } }}>
          <Typography variant="h4" sx={{ fontWeight: 700, color: 'white', mb: 1 }}>
            Order Status
          </Typography>
          <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
            Track and manage procedure order status
          </Typography>
        </CardContent>
      </HeaderCard>
      
      <Table>
        <TableHead>
          <TableRow>
            {/* <TableCell>Order ID</TableCell> */}
            <TableCell>Patient</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Stepper</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {orders.map(order => {
            const statusSteps = (ORDER_TYPE_STATUS_FLOW[order.testType] || []).filter(Boolean);
            return (
              <TableRow key={order.id}>
                {/* <TableCell>{order.id}</TableCell> */}
                <TableCell>{patientMap[order.patientId] || order.patientId}</TableCell>
                <TableCell>{order.status}</TableCell>
                <TableCell>
                  <Stepper activeStep={statusSteps.indexOf(order.status)} alternativeLabel>
                    {statusSteps.map(label => (
                      <Step key={label}>
                        <StepLabel>{label.replace('_', ' ')}</StepLabel>
                      </Step>
                    ))}
                  </Stepper>
                </TableCell>
                <TableCell>
                  <Button onClick={() => handleOpen(order)}>Update Status</Button>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>Update Order Status</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="dense">
            <InputLabel>Status</InputLabel>
            <Select value={newStatus} onChange={handleStatusChange} label="Status">
              {(selectedOrder ? (ORDER_TYPE_STATUS_FLOW[selectedOrder.testType] || []) : []).filter(Boolean).map(status => (
                <MenuItem key={status} value={status}>{status.replace('_', ' ')}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleUpdateStatus} variant="contained">Update</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
} 